import React, { useCallback, useMemo } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Button,
  Typography,
  FormControlLabel,
  Switch,
  useTheme,
  useMediaQuery,
  Grid,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import BlockIcon from '@mui/icons-material/Block';
import PriceChangeIcon from '@mui/icons-material/PriceChange';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import StorefrontIcon from '@mui/icons-material/Storefront';
import HideImageIcon from '@mui/icons-material/HideImage';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import WarehouseIcon from '@mui/icons-material/Warehouse';
import MonitorWeightIcon from '@mui/icons-material/MonitorWeight';
import PercentIcon from '@mui/icons-material/Percent';
import NumbersIcon from '@mui/icons-material/Numbers';
import TuneIcon from '@mui/icons-material/Tune';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import type { InventoryItem, TableColumn, Order, WarehouseStockDto } from '../types/inventory.types';
import AccountingDataTable from '../../shared/AccountingDataTable';
import type { DataTableColumn } from '../../shared/AccountingDataTable';
import { renderImageCell } from '../utils/tableCellUtils';
import { useTranslation } from 'react-i18next';

interface InventoryTableProps {
  // Veriler
  paginatedItems: InventoryItem[];
  hierarchicalItems: InventoryItem[];
  visibleColumns: TableColumn[];
  
  // Toplu İşlem
  bulkActionMode: boolean;
  selectedItems: string[];
  onSelectItem: (id: string) => void;
  onSelectAll: (event: React.ChangeEvent<HTMLInputElement>) => void;
  
  // ✅ YENİ: Mobil için seçili toplu işlem
  selectedBulkAction?: string | null;
  
  // ✅ YENİ: Hierarchical view için
  expandedRows: Set<string>;
  onToggleRow: (itemId: string) => void;  
  // Toplu İşlem Butonları
  onBulkDelete: () => void;
  onBulkInactivate: () => void;
  onBulkPriceUpdate: () => void;
  onBulkPurchasePriceUpdate: () => void;
  onBulkOpenForEcommerce: () => void;
  onBulkCloseForEcommerce: () => void;
  onDeleteAllItems: () => void;
  onDeleteAllProducts: () => void;
  onBulkDeleteImages: () => void;
  onBulkSendToQRMenu: () => void;
  onBulkSendToKiosk: () => void;
  onBulkSendToScale: () => void;
  onBulkAssignWarehouse: () => void;
  onBulkUpdateVatRate: () => void;
  onBulkUpdateDepartmentNo: () => void;
  onBulkUpdateVatAndDept: () => void;
  
  // Depo filtresi (toplu silme mesajı için)
  selectedWarehouse: { id: string; name: string } | null;
  
  // Sıralama
  order: Order;
  orderBy: string | null;
  onRequestSort: (property: string) => void;
  
  // Pagination
  page: number;
  rowsPerPage: number;
  totalCount: number; // ✅ YENİ: Server'dan gelen toplam ürün sayısı
  onPageChange: (event: React.MouseEvent<HTMLButtonElement> | null, newPage: number) => void;
  onRowsPerPageChange: (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  
  // Switches (yeni eklendi)
  hideEmptyStock: boolean;
  onHideEmptyStockChange: (checked: boolean) => void;
  hideInactiveItems: boolean;
  onHideInactiveItemsChange: (checked: boolean) => void;
  sortNewestFirst: boolean; // ✅ YENİ
  onSortNewestFirstChange: (checked: boolean) => void; // ✅ YENİ
  hideZeroPrice: boolean; // ✅ YENİ: Satış fiyatı 0 olanları gizle
  onHideZeroPriceChange: (checked: boolean) => void; // ✅ YENİ
  showVariantChildren: boolean; // ✅ YENİ: Alt varyantları da listede göster
  onShowVariantChildrenChange: (checked: boolean) => void; // ✅ YENİ
  
  // Hücre render fonksiyonu
  getCellValue: (item: InventoryItem, columnId: string, rowIndex?: number) => React.ReactNode;
  localizeUnit: (unitValue?: string | null) => string;
  
  // Resim tıklama - item, event ve resim var mı bilgisini gönderir
  onImageClick: (item: InventoryItem, event: React.MouseEvent<HTMLElement>, hasImage: boolean) => void;
  onWarehouseStockQuantityClick?: (item: InventoryItem, stock: WarehouseStockDto) => void;
}

export const InventoryTable: React.FC<InventoryTableProps> = ({
  paginatedItems,
  hierarchicalItems,
  visibleColumns,
  bulkActionMode,
  selectedItems,
  onSelectItem,
  onSelectAll,
  selectedBulkAction, // ✅ YENİ
  expandedRows,
  onToggleRow,  onBulkDelete,
  onBulkInactivate,
  onBulkPriceUpdate,
  onBulkPurchasePriceUpdate,
  onBulkOpenForEcommerce,
  onBulkCloseForEcommerce,
  onDeleteAllItems,
  onDeleteAllProducts,
  onBulkDeleteImages,
  onBulkSendToQRMenu,
  onBulkSendToKiosk,
  onBulkSendToScale,
  onBulkAssignWarehouse,
  onBulkUpdateVatRate,
  onBulkUpdateDepartmentNo,
  onBulkUpdateVatAndDept,
  selectedWarehouse,
  order,
  orderBy,
  onRequestSort,
  page,
  rowsPerPage,
  totalCount, // ✅ YENİ: Destructure edildi
  onPageChange,
  onRowsPerPageChange,
  hideEmptyStock,
  onHideEmptyStockChange,
  hideInactiveItems,
  onHideInactiveItemsChange,
  sortNewestFirst, // ✅ YENİ
  onSortNewestFirstChange, // ✅ YENİ
  hideZeroPrice, // ✅ YENİ
  onHideZeroPriceChange, // ✅ YENİ
  showVariantChildren, // ✅ YENİ
  onShowVariantChildrenChange, // ✅ YENİ
  getCellValue,
  localizeUnit,
  onImageClick,
  onWarehouseStockQuantityClick,
}) => {
  const { t } = useTranslation('accounting');
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const getColumnLabel = useCallback((columnId: string, fallback: string) => {
    const keyMap: Record<string, string> = {
      rowNumber: 'stock.table.columns.rowNumber',
      name: 'stock.table.columns.name',
      barcode: 'stock.table.columns.barcode',
      stockCode: 'stock.table.columns.stockCode',
      category: 'stock.table.columns.category',
      brand: 'stock.table.columns.brand',
      branch: 'stock.table.columns.branch',
      quantity: 'stock.table.columns.quantity',
      unit: 'stock.table.columns.unit',
      salePrice: 'stock.table.columns.salePrice',
      purchasePrice: 'stock.table.columns.purchasePrice',
      vatRate: 'stock.table.columns.vatRate',
      vatStatus: 'stock.table.columns.vatStatus',
      sctRate: 'stock.table.columns.sctRate',
      sctStatus: 'stock.table.columns.sctStatus',
      warehouse: 'stock.table.columns.warehouse',
      location: 'stock.table.columns.location',
      description: 'stock.table.columns.description',
      ecommerce: 'stock.table.columns.ecommerce',
      qrmenu: 'stock.table.columns.qrmenu',
      departmentNo: 'stock.table.columns.departmentNo',
      canBeNegative: 'stock.table.columns.canBeNegative',
      actions: 'stock.table.columns.actions',
    };
    const key = keyMap[columnId];
    return key ? t(key) : fallback;
  }, [t]);

  // ─── Kolon Tanımları ───────────────────────────────
  const dataTableColumns = useMemo<DataTableColumn<InventoryItem>[]>(() => {
    const cols: DataTableColumn<InventoryItem>[] = [
      // Sıra kolonu (EN BAŞTA)
      {
        id: 'rowNumber',
        label: t('stock.table.columns.rowNumber'),
        width: '60px',
        align: 'right',
        render: (_row, index) => (page * rowsPerPage) + index + 1,
      },
      // Expand/Collapse kolonu (Sıra'dan sonra)
      {
        id: '_expand',
        label: '',
        width: '50px',
        align: 'center',
        render: (row) => {
          const hasStocks = !!(row.warehouseStocks && row.warehouseStocks.length > 0);          if (!hasStocks) return null;
          const isExp = expandedRows.has(row.id);
          return (
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                onToggleRow(row.id);
              }}
            >
              {isExp ? <KeyboardArrowDownIcon /> : <KeyboardArrowRightIcon />}
            </IconButton>
          );
        },
      },
      // Resim kolonu
      {
        id: 'image',
        label: t('stock.table.columns.image'),
        width: '60px',
        align: 'center',
        render: (row) => renderImageCell(row, onImageClick),
      },
    ];

    // Dinamik kolonlar (visibleColumns → DataTableColumn)
    for (const col of visibleColumns.filter(c => c.id !== 'rowNumber')) {
      const isRightAligned = ['actions', 'salePrice', 'quantity', 'rowNumber', 'unit', 'vatRate'].includes(col.id);

      cols.push({
        id: col.id,
        label: getColumnLabel(col.id, col.label),
        sortable: col.sortable,
        field: col.field as keyof InventoryItem,
        align: isRightAligned ? 'right' : 'left',
        ...(col.id === 'name' ? { minWidth: '180px', width: '250px' } : {}),
        ...(col.id === 'description' ? { cellSx: { whiteSpace: 'normal' } } : {}),
        ...(col.id === 'actions' ? {
          cellSx: {
            position: 'sticky',
            right: 0,
            zIndex: 1,
            bgcolor: 'white',
            '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.04)' },
          },
        } : {}),
        render: (row, index) => getCellValue(row, col.id, index),
      });
    }

    return cols;
  }, [visibleColumns, getCellValue, onImageClick, page, rowsPerPage, expandedRows, onToggleRow, t, getColumnLabel]);

  // ─── Genişletilmiş satır içeriği (Depo Stok Detayları) ────
  const renderExpandedContentFn = useMemo(() => {
    return (item: InventoryItem) => {
      if (!item.warehouseStocks || item.warehouseStocks.length === 0) return null;
      const visibleStocks = item.warehouseStocks.filter((ws) => Number(ws.quantity || 0) !== 0);
      if (visibleStocks.length === 0) return null;
      const stocksByWarehouse = visibleStocks.reduce<Record<string, typeof visibleStocks>>((acc, ws) => {
        const key = ws.warehouseId || 'unknown';
        if (!acc[key]) acc[key] = [];
        acc[key].push(ws);
        return acc;
      }, {});
      const warehouseGroups = Object.entries(stocksByWarehouse)
        .map(([warehouseId, stocks]) => ({
          warehouseId,
          warehouseName: stocks[0]?.warehouseName || warehouseId,
          stocks,
          total: stocks.reduce((sum, s) => sum + Number(s.quantity || 0), 0)
        }))
        .sort((a, b) => a.warehouseName.localeCompare(b.warehouseName, 'tr'));

      return (
        <Box sx={{ margin: 1, bgcolor: 'rgba(0, 0, 0, 0.02)', borderRadius: 1, p: 2 }}>
          <Typography variant="subtitle2" gutterBottom component="div" sx={{ fontWeight: 'bold', mb: 2 }}>
            {t('stock.table.expanded.stockDetails')}
          </Typography>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold', bgcolor: 'rgba(0, 0, 0, 0.03)' }}>{t('stock.table.columns.warehouse')}</TableCell>
                <TableCell sx={{ fontWeight: 'bold', bgcolor: 'rgba(0, 0, 0, 0.03)' }}>{t('stock.table.columns.quantity')}</TableCell>
                <TableCell sx={{ fontWeight: 'bold', bgcolor: 'rgba(0, 0, 0, 0.03)' }}>{t('stock.table.columns.location')}</TableCell>
                <TableCell sx={{ fontWeight: 'bold', bgcolor: 'rgba(0, 0, 0, 0.03)' }} align="right">{t('stock.table.columns.salePrice')}</TableCell>
                <TableCell sx={{ fontWeight: 'bold', bgcolor: 'rgba(0, 0, 0, 0.03)' }} align="right">{t('stock.table.columns.purchasePrice')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {warehouseGroups.flatMap((group) =>
                group.stocks.map((ws) => (
                  <TableRow key={`${item.id}-${ws.id || `${ws.warehouseId}-${ws.locationId || 'noloc'}`}`}>
                    <TableCell component="th" scope="row">
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <StorefrontIcon fontSize="small" color="action" />
                        {ws.warehouseName || ws.warehouseId}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 'bold',
                          color: ws.quantity <= 0 ? 'error.main' : 'success.main',
                          cursor: 'pointer',
                          '&:hover': { textDecoration: 'underline', color: 'primary.main' }
                        }}
                        onClick={() => onWarehouseStockQuantityClick?.(item, ws)}
                      >
                        {ws.quantity} {localizeUnit(item.unit || 'adet')}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {ws.locationCode
                        ? `${ws.locationCode}${ws.locationName ? ` - ${ws.locationName}` : ''}`
                        : (ws.locationName || '-')}
                    </TableCell>
                    <TableCell align="right">
                      {item.salePrice} {item.saleCurrency || 'TRY'}
                    </TableCell>
                    <TableCell align="right">
                      {item.purchasePrice || 0} {item.purchaseCurrency || 'TRY'}
                    </TableCell>
                  </TableRow>
                ))
              )}
              {warehouseGroups.map((group) => (
                <TableRow key={`${item.id}-warehouse-total-${group.warehouseId}`} sx={{ bgcolor: 'rgba(0, 0, 0, 0.03)' }}>
                  <TableCell sx={{ fontWeight: 'bold' }}>{group.warehouseName} Toplam</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>
                    {group.total} {localizeUnit(item.unit || 'adet')}
                  </TableCell>
                  <TableCell>-</TableCell>
                  <TableCell align="right">-</TableCell>
                  <TableCell align="right">-</TableCell>
                </TableRow>
              ))}
              {/* Toplam Satırı */}
              <TableRow sx={{ bgcolor: 'rgba(25, 118, 210, 0.08)' }}>
                <TableCell sx={{ fontWeight: 'bold' }}>{t('stock.table.expanded.total')}</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>
                  {visibleStocks.reduce((sum, ws) => sum + ws.quantity, 0)} {localizeUnit(item.unit || 'adet')}
                </TableCell>
                <TableCell>-</TableCell>
                <TableCell align="right">-</TableCell>
                <TableCell align="right">-</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </Box>
      );
    };
  }, [localizeUnit, t]);

  // ─── Satırın genişletilebilir olup olmadığı ────────
  const isExpandable = useMemo(() => {
    return (item: InventoryItem) =>
      !!(item.warehouseStocks && item.warehouseStocks.some((ws) => Number(ws.quantity || 0) !== 0));
  }, []);
  
  return (
    <Paper elevation={3} sx={{ borderRadius: 2, overflow: 'hidden' }}>
      {/* ✅ ÜST SAYFALAMA - Toplu işlem modunda değilken göster (Sadece Desktop) */}
      {!bulkActionMode && !isMobile && (
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between', 
          borderBottom: '1px solid rgba(224, 224, 224, 1)',
          minHeight: '40px',
          height: '40px',
        }}>
          <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', pl: 1 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={hideEmptyStock}
                  onChange={(e) => onHideEmptyStockChange(e.target.checked)}
                  color="primary"
                  size="small"
                />
              }
              label={<Typography variant="caption">{hideEmptyStock ? t('stock.table.toggles.showOutOfStock') : t('stock.table.toggles.hideOutOfStock')}</Typography>}
              sx={{ margin: 0 }}
            />
            
            <FormControlLabel
              control={
                <Switch
                  checked={hideInactiveItems}
                  onChange={(e) => onHideInactiveItemsChange(e.target.checked)}
                  color="primary"
                  size="small"
                />
              }
              label={<Typography variant="caption">{hideInactiveItems ? t('stock.table.toggles.showInactive') : t('stock.table.toggles.hideInactive')}</Typography>}
              sx={{ margin: 0 }}
            />
            
            <FormControlLabel
              control={
                <Switch
                  checked={sortNewestFirst}
                  onChange={(e) => onSortNewestFirstChange(e.target.checked)}
                  color="primary"
                  size="small"
                />
              }
              label={<Typography variant="caption">{t('stock.table.toggles.sortNewestFirst')}</Typography>}
              sx={{ margin: 0 }}
            />
            
            <FormControlLabel
              control={
                <Switch
                  checked={hideZeroPrice}
                  onChange={(e) => onHideZeroPriceChange(e.target.checked)}
                  color="primary"
                  size="small"
                />
              }
              label={<Typography variant="caption">{hideZeroPrice ? t('stock.table.toggles.showZeroPrice') : t('stock.table.toggles.hideZeroPrice')}</Typography>}
              sx={{ margin: 0 }}
            />

            <FormControlLabel
              control={
                <Switch
                  checked={showVariantChildren}
                  onChange={(e) => onShowVariantChildrenChange(e.target.checked)}
                  color="primary"
                  size="small"
                />
              }
              label={<Typography variant="caption">{showVariantChildren ? t('stock.table.toggles.hideVariantChildren') : t('stock.table.toggles.showVariantChildren')}</Typography>}
              sx={{ margin: 0 }}
            />
          </Box>
          
          <TablePagination
            component="div"
            count={totalCount}
            page={page}
            onPageChange={onPageChange}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={onRowsPerPageChange}
            rowsPerPageOptions={[25, 50, 100, 200, 300, 400, 500]}
            labelRowsPerPage={t('stock.pagination.rowsPerPage')}
            labelDisplayedRows={({ from, to, count }) => t('stock.pagination.displayedRows', { from, to, count })}
            sx={{
              '& .MuiTablePagination-toolbar': {
                minHeight: '40px',
                height: '40px',
                padding: '0 8px',
              },
              '& .MuiTablePagination-selectLabel': {
                margin: 0,
              },
              '& .MuiTablePagination-displayedRows': {
                margin: 0,
              }
            }}
          />
        </Box>
      )}
      
      {/* Toplu İşlemler için butonlar */}
      {bulkActionMode && (
        <Box sx={{ 
          p: { xs: 1.5, md: 1 }, 
          pl: { xs: 1.5, md: 2 }, 
          display: 'flex', 
          flexDirection: { xs: 'column', md: 'row' },
          alignItems: { xs: 'stretch', md: 'center' }, 
          borderBottom: '1px solid rgba(224, 224, 224, 1)',
          gap: { xs: 1, md: 0 }
        }}>
          {/* Desktop: Tüm butonlar */}
          <Box sx={{ display: { xs: 'none', md: 'flex' }, gap: 1, flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              color="error"
              size="small"
              startIcon={<DeleteIcon />}
              onClick={onBulkDelete}
              disabled={selectedItems.length === 0}
            >
              {t('stock.bulkActions.delete', { count: selectedItems.length })}
            </Button>
            <Button
              variant="contained"
              color="warning"
              size="small"
              startIcon={<BlockIcon />}
              onClick={onBulkInactivate}
              disabled={selectedItems.length === 0}
            >
              {t('stock.bulkActions.inactivate', { count: selectedItems.length })}
            </Button>
            <Button
              variant="contained"
              color="primary"
              size="small"
              startIcon={<PriceChangeIcon />}
              onClick={onBulkPriceUpdate}
              disabled={selectedItems.length === 0}
            >
              {t('stock.bulkActions.updateSalePrice', { count: selectedItems.length })}
            </Button>
            <Button
              variant="contained"
              color="info"
              size="small"
              startIcon={<MonetizationOnIcon />}
              onClick={onBulkPurchasePriceUpdate}
              disabled={selectedItems.length === 0}
            >
              {t('stock.bulkActions.updatePurchasePrice', { count: selectedItems.length })}
            </Button>
            <Button
              variant="contained"
              color="success"
              size="small"
              startIcon={<StorefrontIcon />}
              onClick={onBulkOpenForEcommerce}
              disabled={selectedItems.length === 0}
            >
              {t('stock.bulkActions.openEcommerce', { count: selectedItems.length })}
            </Button>
            <Button
              variant="contained"
              color="warning"
              size="small"
              startIcon={<StorefrontIcon />}
              onClick={onBulkCloseForEcommerce}
              disabled={selectedItems.length === 0}
            >
              {t('stock.bulkActions.closeEcommerce', { count: selectedItems.length })}
            </Button>
            <Button
              variant="contained"
              color="success"
              size="small"
              startIcon={<StorefrontIcon />}
              onClick={onBulkSendToQRMenu}
              disabled={selectedItems.length === 0}
            >
              {t('stock.bulkActions.openQrMenu', { count: selectedItems.length })}
            </Button>
            <Button
              variant="contained"
              color="info"
              size="small"
              startIcon={<StorefrontIcon />}
              onClick={onBulkSendToKiosk}
              disabled={selectedItems.length === 0}
            >
              {t('stock.bulkActions.openKiosk', { count: selectedItems.length })}
            </Button>
            <Button
              variant="contained"
              color="secondary"
              size="small"
              startIcon={<MonitorWeightIcon />}
              onClick={onBulkSendToScale}
              disabled={selectedItems.length === 0}
            >
              Tartıya Gönder ({selectedItems.length})
            </Button>
            <Button
              variant="contained"
              color="primary"
              size="small"
              startIcon={<WarehouseIcon />}
              onClick={onBulkAssignWarehouse}
              disabled={selectedItems.length === 0}
            >
              {t('stock.bulkActions.assignWarehouse', { count: selectedItems.length })}
            </Button>
            <Button
              variant="contained"
              color="primary"
              size="small"
              startIcon={<PercentIcon />}
              onClick={onBulkUpdateVatRate}
              disabled={selectedItems.length === 0}
            >
              {t('stock.bulkActions.updateVat', { count: selectedItems.length })}
            </Button>
            <Button
              variant="contained"
              color="info"
              size="small"
              startIcon={<NumbersIcon />}
              onClick={onBulkUpdateDepartmentNo}
              disabled={selectedItems.length === 0}
            >
              {t('stock.bulkActions.updateDepartmentNo', { count: selectedItems.length })}
            </Button>
            <Button
              variant="contained"
              color="secondary"
              size="small"
              startIcon={<TuneIcon />}
              onClick={onBulkUpdateVatAndDept}
              disabled={selectedItems.length === 0}
            >
              {t('stock.bulkActions.updateVatAndDepartmentNo', { count: selectedItems.length })}
            </Button>
            {/* Ürün seçimi gerektirmez: şirketin tüm ürünlerini kalıcı siler */}
            <Button
              variant="contained"
              color="error"
              size="small"
              startIcon={<DeleteForeverIcon />}
              onClick={onDeleteAllProducts}
              sx={{ ml: 'auto' }}
            >
              Tüm Ürünleri Sil (Kalıcı)
            </Button>
          </Box>
          
          {/* Mobil: Sadece seçili işlem butonu */}
          <Box sx={{ display: { xs: 'block', md: 'none' } }}>
            {selectedBulkAction === 'delete' && (
              <Button
                fullWidth
                variant="contained"
                color="error"
                startIcon={<DeleteIcon />}
                onClick={onBulkDelete}
                disabled={selectedItems.length === 0}
              >
                {t('stock.bulkActions.delete', { count: selectedItems.length })}
              </Button>
            )}
            {selectedBulkAction === 'inactivate' && (
              <Button
                fullWidth
                variant="contained"
                color="warning"
                startIcon={<BlockIcon />}
                onClick={onBulkInactivate}
                disabled={selectedItems.length === 0}
              >
                {t('stock.bulkActions.inactivate', { count: selectedItems.length })}
              </Button>
            )}
            {selectedBulkAction === 'price' && (
              <Button
                fullWidth
                variant="contained"
                color="success"
                startIcon={<PriceChangeIcon />}
                onClick={onBulkPriceUpdate}
                disabled={selectedItems.length === 0}
              >
                {t('stock.bulkActions.updateSalePrice', { count: selectedItems.length })}
              </Button>
            )}
            {selectedBulkAction === 'purchasePrice' && (
              <Button
                fullWidth
                variant="contained"
                color="info"
                startIcon={<MonetizationOnIcon />}
                onClick={onBulkPurchasePriceUpdate}
                disabled={selectedItems.length === 0}
              >
                {t('stock.bulkActions.updatePurchasePrice', { count: selectedItems.length })}
              </Button>
            )}
            {selectedBulkAction === 'ecommerceOpen' && (
              <Button
                fullWidth
                variant="contained"
                color="success"
                startIcon={<StorefrontIcon />}
                onClick={onBulkOpenForEcommerce}
                disabled={selectedItems.length === 0}
              >
                {t('stock.bulkActions.openEcommerce', { count: selectedItems.length })}
              </Button>
            )}
            {selectedBulkAction === 'ecommerceClose' && (
              <Button
                fullWidth
                variant="contained"
                color="error"
                startIcon={<StorefrontIcon />}
                onClick={onBulkCloseForEcommerce}
                disabled={selectedItems.length === 0}
              >
                {t('stock.bulkActions.closeEcommerce', { count: selectedItems.length })}
              </Button>
            )}
            {selectedBulkAction === 'qrmenu' && (
              <Button
                fullWidth
                variant="contained"
                color="secondary"
                startIcon={<PhotoCameraIcon />}
                onClick={onBulkSendToQRMenu}
                disabled={selectedItems.length === 0}
              >
                {t('stock.bulkActions.openQrMenu', { count: selectedItems.length })}
              </Button>
            )}
            {selectedBulkAction === 'kiosk' && (
              <Button
                fullWidth
                variant="contained"
                color="secondary"
                startIcon={<PhotoCameraIcon />}
                onClick={onBulkSendToKiosk}
                disabled={selectedItems.length === 0}
              >
                {t('stock.bulkActions.openKiosk', { count: selectedItems.length })}
              </Button>
            )}
            {selectedBulkAction === 'scale' && (
              <Button
                fullWidth
                variant="contained"
                color="secondary"
                startIcon={<MonitorWeightIcon />}
                onClick={onBulkSendToScale}
                disabled={selectedItems.length === 0}
              >
                Tartıya Gönder ({selectedItems.length})
              </Button>
            )}
            {selectedBulkAction === 'deleteImages' && (
              <Button
                fullWidth
                variant="contained"
                color="error"
                startIcon={<HideImageIcon />}
                onClick={onBulkDeleteImages}
                disabled={selectedItems.length === 0}
              >
                {t('stock.bulkActions.deleteImages', { count: selectedItems.length })}
              </Button>
            )}
            {selectedBulkAction === 'assignWarehouse' && (
              <Button
                fullWidth
                variant="contained"
                color="primary"
                startIcon={<WarehouseIcon />}
                onClick={onBulkAssignWarehouse}
                disabled={selectedItems.length === 0}
              >
                {t('stock.bulkActions.assignWarehouse', { count: selectedItems.length })}
              </Button>
            )}
            {selectedBulkAction === 'vatRate' && (
              <Button
                fullWidth
                variant="contained"
                color="secondary"
                startIcon={<PercentIcon />}
                onClick={onBulkUpdateVatRate}
                disabled={selectedItems.length === 0}
              >
                {t('stock.bulkActions.updateVat', { count: selectedItems.length })}
              </Button>
            )}
            {selectedBulkAction === 'departmentNo' && (
              <Button
                fullWidth
                variant="contained"
                color="secondary"
                startIcon={<NumbersIcon />}
                onClick={onBulkUpdateDepartmentNo}
                disabled={selectedItems.length === 0}
              >
                {t('stock.bulkActions.updateDepartmentNo', { count: selectedItems.length })}
              </Button>
            )}
            {selectedBulkAction === 'vatAndDept' && (
              <Button
                fullWidth
                variant="contained"
                color="secondary"
                startIcon={<TuneIcon />}
                onClick={onBulkUpdateVatAndDept}
                disabled={selectedItems.length === 0}
              >
                {t('stock.bulkActions.updateVatAndDepartmentNo', { count: selectedItems.length })}
              </Button>
            )}
            {selectedBulkAction === 'deleteAll' && (
              <Button
                fullWidth
                variant="contained"
                color="error"
                startIcon={<DeleteIcon />}
                onClick={onDeleteAllItems}
                disabled={selectedItems.length === 0}
              >
                {t('stock.bulkActions.deleteAll', { count: selectedItems.length })}
              </Button>
            )}
          </Box>
          
          {/* Desktop: Seçim sayısı */}
          <Typography variant="body2" color="text.secondary" sx={{ ml: 2, display: { xs: 'none', md: 'block' } }}>
            {selectedItems.length > 0 
              ? t('stock.bulkActions.selectedCount', { count: selectedItems.length })
              : t('stock.bulkActions.selectItemsPrompt')}
          </Typography>
        </Box>
      )}
      
      {/* ─── Ana Tablo (AccountingDataTable ile) ─── */}
      <AccountingDataTable<InventoryItem>
        columns={dataTableColumns}
        rows={paginatedItems}
        getRowId={(item) => item.id}
        pagination={false}
        elevation={0}
        tableLayout="auto"
        ariaLabel={t('stock.table.ariaLabel')}
        // Server-side sıralama
        onSort={(field) => onRequestSort(field)}
        sortField={orderBy || undefined}
        sortDirection={order}
        // Seçim (Checkbox)
        selectable={bulkActionMode}
        selectedIds={selectedItems}
        onSelectId={onSelectItem}
        onSelectAll={onSelectAll}
        // Genişletilebilir satırlar
        expandedRowIds={expandedRows}
        onToggleExpand={onToggleRow}
        renderExpandedContent={renderExpandedContentFn}
        isExpandable={isExpandable}
        showExpandColumn={false}
        // Boş durum
        emptyMessage={t('stock.table.noData')}
        columnResizeStorageKey="inventory-table"
      />
      
      {/* ALT SAYFALAMA ve Switch'ler */}
      <Box sx={{ 
        display: 'flex', 
        flexDirection: { xs: 'column', md: 'row' },
        alignItems: { xs: 'stretch', md: 'center' }, 
        justifyContent: { xs: 'center', md: 'space-between' }, 
        borderTop: '1px solid rgba(224, 224, 224, 1)',
        minHeight: { xs: 'auto', md: '40px' },
        py: { xs: 2, md: 0 },
        gap: { xs: 2, md: 0 },
      }}>
        {/* Desktop: Switch'ler solda */}
        <Box sx={{ display: { xs: 'none', md: 'flex' }, gap: 1.5, alignItems: 'center', pl: 1 }}>
          <FormControlLabel
            control={
              <Switch
                checked={hideEmptyStock}
                onChange={(e) => onHideEmptyStockChange(e.target.checked)}
                color="primary"
                size="small"
              />
            }
            label={<Typography variant="caption">{hideEmptyStock ? t('stock.table.toggles.showOutOfStock') : t('stock.table.toggles.hideOutOfStock')}</Typography>}
            sx={{ margin: 0 }}
          />
          
          <FormControlLabel
            control={
              <Switch
                checked={hideInactiveItems}
                onChange={(e) => onHideInactiveItemsChange(e.target.checked)}
                color="primary"
                size="small"
              />
            }
            label={<Typography variant="caption">{hideInactiveItems ? t('stock.table.toggles.showInactive') : t('stock.table.toggles.hideInactive')}</Typography>}
            sx={{ margin: 0 }}
          />
          
          <FormControlLabel
            control={
              <Switch
                checked={sortNewestFirst}
                onChange={(e) => onSortNewestFirstChange(e.target.checked)}
                color="primary"
                size="small"
              />
            }
            label={<Typography variant="caption">{t('stock.table.toggles.sortNewestFirst')}</Typography>}
            sx={{ margin: 0 }}
          />
          
          <FormControlLabel
            control={
              <Switch
                checked={hideZeroPrice}
                onChange={(e) => onHideZeroPriceChange(e.target.checked)}
                color="primary"
                size="small"
              />
            }
            label={<Typography variant="caption">{hideZeroPrice ? t('stock.table.toggles.showZeroPrice') : t('stock.table.toggles.hideZeroPrice')}</Typography>}
            sx={{ margin: 0 }}
          />

          <FormControlLabel
            control={
              <Switch
                checked={showVariantChildren}
                onChange={(e) => onShowVariantChildrenChange(e.target.checked)}
                color="primary"
                size="small"
              />
            }
            label={<Typography variant="caption">{showVariantChildren ? t('stock.table.toggles.hideVariantChildren') : t('stock.table.toggles.showVariantChildren')}</Typography>}
            sx={{ margin: 0 }}
          />
        </Box>
        
        {/* Sayfalama - Desktop: sağda, Mobil: üstte */}
        <TablePagination
          component="div"
          count={totalCount}
          page={page}
          onPageChange={onPageChange}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={onRowsPerPageChange}
          rowsPerPageOptions={[25, 50, 100, 200, 300, 400, 500]}
          labelRowsPerPage={isMobile ? '' : t('stock.pagination.rowsPerPage')}
          labelDisplayedRows={({ from, to, count }) => t('stock.pagination.displayedRows', { from, to, count })}
          sx={{
            width: { xs: '100%', md: 'auto' },
            '& .MuiTablePagination-toolbar': {
              minHeight: '40px',
              height: '40px',
              padding: { xs: '0 4px', md: '0 8px' },
            },
            '& .MuiTablePagination-selectLabel': {
              margin: 0,
              display: { xs: 'none', md: 'block' },
            },
            '& .MuiTablePagination-displayedRows': {
              margin: 0,
            }
          }}
        />

        {/* Mobil: Switch'ler altta, 2 sütun */}
        <Grid container spacing={1} sx={{ display: { xs: 'grid', md: 'none' }, gridTemplateColumns: 'repeat(2, 1fr)', px: 2 }}>
          <Grid item xs={6}>
            <FormControlLabel
              control={
                <Switch
                  checked={hideEmptyStock}
                  onChange={(e) => onHideEmptyStockChange(e.target.checked)}
                  color="primary"
                  size="small"
                />
              }
              label={<Typography variant="caption" sx={{ fontSize: '0.7rem' }}>{hideEmptyStock ? t('stock.table.toggles.showOutOfStock') : t('stock.table.toggles.hideOutOfStock')}</Typography>}
              sx={{ margin: 0 }}
            />
          </Grid>
          
          <Grid item xs={6}>
            <FormControlLabel
              control={
                <Switch
                  checked={hideInactiveItems}
                  onChange={(e) => onHideInactiveItemsChange(e.target.checked)}
                  color="primary"
                  size="small"
                />
              }
              label={<Typography variant="caption" sx={{ fontSize: '0.7rem' }}>{hideInactiveItems ? t('stock.table.toggles.showInactive') : t('stock.table.toggles.hideInactive')}</Typography>}
              sx={{ margin: 0 }}
            />
          </Grid>
          
          <Grid item xs={6}>
            <FormControlLabel
              control={
                <Switch
                  checked={sortNewestFirst}
                  onChange={(e) => onSortNewestFirstChange(e.target.checked)}
                  color="primary"
                  size="small"
                />
              }
              label={<Typography variant="caption" sx={{ fontSize: '0.7rem' }}>{t('stock.table.toggles.sortNewestFirst')}</Typography>}
              sx={{ margin: 0 }}
            />
          </Grid>
          
          <Grid item xs={6}>
            <FormControlLabel
              control={
                <Switch
                  checked={hideZeroPrice}
                  onChange={(e) => onHideZeroPriceChange(e.target.checked)}
                  color="primary"
                  size="small"
                />
              }
              label={<Typography variant="caption" sx={{ fontSize: '0.7rem' }}>{hideZeroPrice ? t('stock.table.toggles.showZeroPrice') : t('stock.table.toggles.hideZeroPrice')}</Typography>}
              sx={{ margin: 0 }}
            />
          </Grid>

          <Grid item xs={6}>
            <FormControlLabel
              control={
                <Switch
                  checked={showVariantChildren}
                  onChange={(e) => onShowVariantChildrenChange(e.target.checked)}
                  color="primary"
                  size="small"
                />
              }
              label={<Typography variant="caption" sx={{ fontSize: '0.7rem' }}>{showVariantChildren ? t('stock.table.toggles.hideVariantChildren') : t('stock.table.toggles.showVariantChildren')}</Typography>}
              sx={{ margin: 0 }}
            />
          </Grid>
        </Grid>
      </Box>
    </Paper>
  );
};

